import { Badge } from '@/components/ui/badge'
import { Link } from '@/components/ui/link'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import type { TypedTo } from '@/router'

import * as S from './count-badge-tooltip.styled'

const DEFAULT_ITEMS_LIMIT = 8

interface CountBadgeTooltipProps {
  badgeLabel: string
  items: string[]
  limit?: number
  // Note: without it the list is shown whole, with no "more" row
  moreLabel?: (count: number) => string
  // Note: caps the list height and lets it scroll; the tooltip stays open while the pointer is over it
  scrollable?: boolean
  moreTo?: TypedTo
  // Note: makes the "more" row a button, e.g. to open a popup with the whole list
  onMoreClick?: VoidFunction
  // Note: 'click' opens the list only on click, and the click does not reach a row around it
  openOn?: 'click' | 'hover'
  // Note: 'text' renders the label as plain small text instead of a badge, e.g. inside a subtitle
  variant?: 'badge' | 'text'
}

export const CountBadgeTooltip = ({
  badgeLabel,
  items,
  limit = DEFAULT_ITEMS_LIMIT,
  moreLabel,
  moreTo,
  onMoreClick,
  openOn = 'hover',
  scrollable = false,
  variant = 'badge',
}: CountBadgeTooltipProps) => (
  <Tooltip
    interactive={!!moreTo || !!onMoreClick || scrollable}
    openOn={openOn}
    trigger={
      variant === 'text' ? (
        <Typography as="span" color="black500" size="text-small">
          {openOn === 'click' ? <S.ClickableText>{badgeLabel}</S.ClickableText> : badgeLabel}
        </Typography>
      ) : (
        <Badge color="blue" size="small" variant="light">
          {badgeLabel}
        </Badge>
      )
    }
    onClick={openOn === 'click' ? (event) => event.stopPropagation() : undefined}
  >
    {(close) => (
      <S.List $scrollable={scrollable}>
        {items.slice(0, limit).map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
        {moreLabel && items.length > limit && (
          <S.More>
            {moreTo ? (
              <Link size="small" to={moreTo} use="unset">
                {moreLabel(items.length - limit)}
              </Link>
            ) : onMoreClick ? (
              <S.MoreButton
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  close()
                  onMoreClick()
                }}
              >
                {moreLabel(items.length - limit)}
              </S.MoreButton>
            ) : (
              moreLabel(items.length - limit)
            )}
          </S.More>
        )}
      </S.List>
    )}
  </Tooltip>
)
